import axios from 'axios';
import i18n from '../i18n';
import { scheduleTokenRefresh } from './tokenManager';
import { cancelPushSubscription } from '../utils/pushCleanup';
import { toIntlLocale } from '../utils/formatters';
import { redirectToLogin } from '../utils/loginRedirect';

const storedUuid = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key))?.uuid;
  } catch (error) {
    console.error(`Failed to parse ${key} data:`, error);
    return undefined;
  }
};

// Create axios instance
const axiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to avoid multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor: Add access token and X-Customer-ID
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const lang = i18n.language || 'pt';
    config.headers['Accept-Language'] = toIntlLocale(lang);

    // Add X-Customer-ID for customer-scoped endpoints
    // Exception: /collaborators/me/ is bootstrap and does NOT require it
    const isCustomerEndpoint = config.url?.includes('/customer/');
    const isBootstrapEndpoint = config.url?.includes('/collaborators/me');
    // crm_service checks the caller's role codes in that customer
    const isCrmEndpoint = config.url?.includes('/crm/');
    // notification_service resolves the caller from both ids
    const isNotificationSettingsEndpoint = config.url?.includes('/notification-settings');

    if ((isCustomerEndpoint && !isBootstrapEndpoint) || isCrmEndpoint || isNotificationSettingsEndpoint) {
      config.headers['X-Customer-ID'] = storedUuid('customer');
    }

    if (isNotificationSettingsEndpoint) {
      config.headers['X-Collaborator-ID'] = storedUuid('collaborator');
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: Handle token refresh and update connection timestamp
axiosInstance.interceptors.response.use(
  (response) => {
    // Update connection timestamp on successful response
    if (window.updateConnectionTimestamp) {
      window.updateConnectionTimestamp();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');

      if (!refreshToken) {
        // No refresh token, logout
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        await cancelPushSubscription();
        redirectToLogin();
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${process.env.REACT_APP_AUTH_API_URL}/token/refresh/`, {
          refresh: refreshToken,
        });

        const { access } = response.data;

        localStorage.setItem('access_token', access);
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${access}`;

        // Schedule next proactive refresh
        scheduleTokenRefresh(access);

        processQueue(null, access);
        isRefreshing = false;

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;

        // Refresh failed, logout
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        await cancelPushSubscription();
        redirectToLogin();

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
